import { useCallback, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { contactsApi } from '../../../api/contacts'
import { RefusedError } from '../../../api/client'
import { useSpoke } from '../../../hooks/useSpoke'
import { formatDate, formatScore } from '../../../lib/format'
import { Avatar } from '../../../components/Avatar'
import { DocumentDrop } from '../../../components/Documents/DocumentDrop'
import { DocumentList } from '../../../components/Documents/DocumentList'
import { LineIcon } from '../../../components/LineIcon'
import { VoteBadge, VoteButtons } from '../../../components/VoteButtons'
import { FactRow } from './FactRow'
import { SitePanel } from './SitePanel'
import { FOUNDATION_SITES, LANES, TRUST_POINTS, countChips, industriesOf, trustAdjustment, trustPoints } from './words'
import { correctionsApi } from '../../../api/corrections'
import './card.css'

// The contact card (REQ-117): the person as a receptor. Everything on file about them sits on a typed site with
// its sources and a status the rep can move — guessed, corroborated, locked in (confirmed and rated), not
// right. Worked outside any opportunity; what it settles is what the workbench later stands on. Nothing here
// sends anything or writes to the model.

export function ContactCardPage() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const { personId = '' } = useParams()
  const q = useQuery({ queryKey: ['contact-card', spoke, personId], queryFn: () => contactsApi.card(spoke, personId), retry: false })
  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['contact-card', spoke, personId] })
    void qc.invalidateQueries({ queryKey: ['contact', spoke, personId] })
    void qc.invalidateQueries({ queryKey: ['contacts', spoke] })
  }, [qc, spoke, personId])
  // the photo (REQ-125): uploaded here, on the card, now that the list opens nothing of its own
  const fileRef = useRef<HTMLInputElement>(null)
  const [stamp, setStamp] = useState(0)
  // THE TRUST GAUGE (Don, 2026-10-12): the strength the mail reads, moved only by saying what happened; the adjustment goes on the
  // corrections ledger for the nightly loop, never over the stored strength
  const [adjusting, setAdjusting] = useState(false)
  const [trustTo, setTrustTo] = useState<number>(5)
  const [trustWhy, setTrustWhy] = useState('')
  const [trustRefused, setTrustRefused] = useState<string | null>(null)
  const [trustRecorded, setTrustRecorded] = useState<string | null>(null)
  const trust = useMutation({
    mutationFn: (body: { entityKind: 'relationship'; entityId: string; fromValue: string | null; toValue: string; reason: string }) => correctionsApi.record(spoke, body),
    onSuccess: (_r, body) => { setAdjusting(false); setTrustWhy(''); setTrustRefused(null); setTrustRecorded(`Recorded: ${body.toValue} of 10. Scout learns from the reason; the mail keeps reading the rest.`) },
    onError: (e) => setTrustRefused(e instanceof RefusedError ? e.refusal.message : e.message),
  })
  const upload = useMutation({
    mutationFn: (file: File) => contactsApi.uploadPhoto(spoke, personId, file),
    onSuccess: () => { setStamp(Date.now()); refresh(); void qc.invalidateQueries({ queryKey: ['account', spoke] }) },
  })

  if (q.isPending) return <p className="muted centered">Reading everything on file…</p>
  if (q.isError || !q.data) {
    const missing = q.error instanceof RefusedError && q.error.status === 404
    return (
      <section className="page contact-card-missing">
        <div className="refusal">
          <strong>{missing ? 'No such contact' : 'Unavailable'}</strong>
          <p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error?.message ?? 'No such contact.'}</p>
          <p><Link to="/contacts">Back to contacts</Link></p>
        </div>
      </section>
    )
  }

  const card = q.data
  const c = card.contact
  const p = c.person
  const strength = p.relationshipStrength
  const line = [p.title, c.companyName, p.location, p.role && p.role !== 'other' ? p.role : null].filter(Boolean).join(' · ')
  const photoUrl = p.photoUrl ? `${p.photoUrl}${stamp ? `?v=${stamp}` : ''}` : null

  return (
    <section className="page contact-card">
      <header className="deals-head">
        <div className="card-identity">
          <div className="card-photo">
            <Avatar name={p.name} photoUrl={photoUrl} persona={p.persona} role={p.role} size={64} />
            <button className="link-button small" onClick={() => fileRef.current?.click()} disabled={upload.isPending} title={p.photoUrl ? `Photo from ${p.photoSource ?? 'an enricher'}${p.photoAt ? `, ${formatDate(p.photoAt)}` : ''}.` : 'No photo on file.'}>
              <LineIcon name="upload" size={12} /> {upload.isPending ? 'Uploading…' : p.photoUrl ? 'Replace' : 'Add a photo'}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = '' }} />
          </div>
          <div className="card-words">
            <p className="kicker"><Link to="/contacts">Contacts</Link>{c.companyName ? ` · ${c.companyName}` : ''}</p>
            <h1>{p.name}</h1>
            <p className="lede">
              {line}{line && strength != null ? ' · ' : ''}
              {strength != null && <span title="How often and how recently you wrote, read from your own mail">{formatScore(strength * 100)}% relationship strength{trustPoints(strength) != null ? ` (${trustPoints(strength)} of 10)` : ''}</span>}
              {c.lastActivity && <span className="muted"> · last touch {formatDate(c.lastActivity)}</span>}
              {' '}<button type="button" className="link-button small" onClick={() => { setAdjusting((v) => !v); setTrustRecorded(null); setTrustTo(trustPoints(strength) ?? 5) }} title="Where the relationship really stands, 1 to 10. Scout asks why; the reason is what it learns from.">{adjusting ? 'Cancel' : 'Adjust trust'}</button>
            </p>
            {trustRecorded && !adjusting && <p className="small muted" role="status">{trustRecorded}</p>}
            {adjusting && (
              <form className="trust-adjust" onSubmit={(e) => { e.preventDefault(); const body = trustAdjustment(personId, strength, trustTo, trustWhy); if ('refused' in body) { setTrustRefused(body.refused); return } setTrustRefused(null); trust.mutate(body) }} aria-label="Adjust the trust gauge">
                <label className="small">Where it really stands
                  <select value={trustTo} onChange={(e) => setTrustTo(Number(e.target.value))} aria-label="Trust, 1 to 10">
                    {TRUST_POINTS.map((n) => <option key={n} value={n}>{n === 1 ? '1 — no relationship' : n === 5 ? '5 — know each other' : n === 10 ? '10 — they would call me first' : String(n)}</option>)}
                  </select>
                </label>
                <label className="small" style={{ flex: '1 1 320px' }}>What just happened? Tell Scout about it.
                  <textarea value={trustWhy} onChange={(e) => setTrustWhy(e.target.value)} placeholder="e.g. They called me first about the carve-out; dinner last week; they went quiet after the proposal." aria-label="What happened" />
                </label>
                <div className="trust-actions">
                  <button type="submit" className="button small primary" disabled={trust.isPending}>{trust.isPending ? 'Recording…' : 'Record'}</button>
                  <span className="muted small">The mail keeps reading the strength; what you say and why go to the nightly loop.</span>
                </div>
                {trustRefused && <p className="error small">{trustRefused}</p>}
              </form>
            )}
            {upload.isError && <p className="error small">{upload.error instanceof RefusedError ? upload.error.refusal.message : upload.error.message}</p>}
            <ul className="card-counts plain">
              {countChips(card.counts).map((chip) => <li key={chip.status} className={`tag status-${chip.status}`}>{chip.text}</li>)}
              {(card.yourVote ?? 0) !== 0 && <li><VoteBadge vote={card.yourVote} /></li>}
            </ul>
          </div>
        </div>
        <div className="actions">
          <VoteButtons spoke={spoke} personId={personId} vote={card.yourVote} />
          {p.companyId && <Link className="button primary" to={`/workbench/${encodeURIComponent(p.companyId)}?person=${encodeURIComponent(p.personId)}`} title="Everyone on file around this account, a conversation that moves the draft, and a message that leaves for your Outbox.">Open the workbench</Link>}
          {p.companyId && <Link className="button" to={`/accounts/${encodeURIComponent(p.companyId)}`}>Account plan</Link>}
        </div>
      </header>

      {card.yourCall.length > 0 && (
        <section className="card your-call">
          <div className="card-head">
            <div>
              <h2>Scout guessed — your call</h2>
              <p className="muted small">A guess has one source behind it. Confirm what you know and rate how useful it is; it is locked in from then on.</p>
            </div>
          </div>
          <ul className="plain your-call-list">
            {card.yourCall.map((f) => <FactRow key={f.assertionId} fact={f} spoke={spoke} personId={personId} showSite onChanged={refresh} />)}
          </ul>
        </section>
      )}

      {card.window && (
        <p className="muted small card-window">
          Read from {card.window.addresses.join(', ')} between {formatDate(card.window.from)} and {formatDate(card.window.to)}.
        </p>
      )}

      {/* THE FOUNDATION: the person and their organisation, above the four Activator lanes (owner, 2026-10-13: contacts are the foundation) */}
      <div className="foundation">
        <p className="kicker foundation-kicker">Contacts are the foundation <span className="muted">· continuing enrichment: the nightly sweep briefs them, the web with two anchors, your call on every fact</span></p>
        <div className="sites foundation-sites">
          {card.sites.filter((s) => FOUNDATION_SITES.includes(s.site)).map((s) => (
            <SitePanel key={s.site} site={s} inCommon={card.inCommon} crmConnected={card.crmConnected} spoke={spoke} personId={personId} onChanged={refresh} />
          ))}
        </div>
      </div>
      {/* THE FOUR LANES of the Activator Advantage: Commit · Connect · Create value · Collaborate; each site under the behaviour it serves */}
      <div className="activator-lanes">
        {LANES.map((lane) => {
          const boards = lane.key === 'create' ? industriesOf(card.sites.flatMap((s) => s.facts).filter((f) => f.key === 'board-of' && f.status !== 'refuted').map((f) => f.value)) : []
          return (
            <section key={lane.key} className={`lane lane-${lane.key}`} aria-label={lane.name}>
              <header className="lane-head">
                <span className="lane-mark" aria-hidden="true">{LANES.indexOf(lane) + 1}</span>
                <div>
                  <h2 className="lane-name">{lane.name}</h2>
                  <p className="muted small lane-line">{lane.line}</p>
                  {boards.length > 0 && <p className="small lane-industries"><strong>Industries they touch:</strong> {boards.join(' · ')} <span className="muted">— from the boards they sit on; bring the firm's knowledge of these</span></p>}
                </div>
              </header>
              {card.sites.filter((s) => lane.sites.includes(s.site)).map((s) => (
                <SitePanel key={s.site} site={s} inCommon={card.inCommon} crmConnected={card.crmConnected} spoke={spoke} personId={personId} onChanged={refresh} />
              ))}
            </section>
          )
        })}
      </div>

      {/* Documents (REQ-119): a deck, a sheet, a proposal, a note — uploaded here, read once, and what it says
          about the person lands on the sites above as facts with the file as their source. */}
      <section className="card documents-card">
        <div className="card-head">
          <div>
            <h2>Documents</h2>
            <p className="muted small">Upload what you have on {p.name} — a deck, a sheet, a proposal, a note. Scout reads it once and what it says lands on the sites above, with the file and the place as the source.</p>
          </div>
        </div>
        <DocumentDrop spoke={spoke} origin="card" targets={{ personId }} onUploaded={refresh} />
        <DocumentList spoke={spoke} targets={{ person: personId }} invalidate={[['contact-card', spoke, personId], ['contact', spoke, personId]]} />
      </section>
    </section>
  )
}
